import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Palette, Upload, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Simple queue for the platform designer. Lists all paid + change-request
 * jobs, links straight into each order, and offers a one-click "Mark
 * Delivered" once the designer has uploaded artwork on the order page.
 */
export default function DesignerQueue() {
  const { token, user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/designer-service/queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load queue');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const markDelivered = async (id) => {
    if (!window.confirm('Confirm the artwork has been uploaded to the order. This will notify the mason.')) return;
    try {
      await axios.post(`${API}/designer-service/${id}/mark-delivered`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Marked as delivered.');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const isDesigner = user?.email === 'memorials@setinstonedidcot.co.uk' || user?.role === 'super_admin';
  if (!isDesigner) {
    return <div className="p-6">Designer access only.</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
      </Button>
      <div className="flex items-center gap-3 mb-6">
        <Palette className="w-8 h-8 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Designer Queue</h1>
          <p className="text-sm text-slate-500">Paid memorial artwork requests waiting for you to design.</p>
        </div>
      </div>

      {loading && <p className="text-slate-500">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="text-slate-500 text-sm">Nothing waiting. Nice — inbox zero.</p>
      )}

      <div className="space-y-3">
        {items.map(r => (
          <Card key={r.id} className={r.status === 'changes_requested' ? 'border-amber-300' : 'border-purple-200'}>
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">#{r.order_number || r.order_id.slice(0, 6)}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                    £{Number(r.fee_amount).toFixed(2)}
                  </span>
                  {r.status === 'changes_requested' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      Change request ({r.changes_used}/{r.changes_free_limit})
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-700 mt-1">
                  {r.company_name || '—'} · {r.requested_by_name} ({r.requested_by_email})
                </p>
                {r.last_change_notes && (
                  <p className="text-xs text-amber-800 mt-2 bg-amber-50 p-2 rounded border border-amber-200">
                    <strong>Change notes:</strong> {r.last_change_notes}
                  </p>
                )}
                {r.notes && !r.last_change_notes && (
                  <p className="text-xs text-slate-600 mt-1"><strong>Notes:</strong> {r.notes}</p>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => navigate(`/orders/${r.order_id}`)}>
                  <Upload className="w-4 h-4 mr-1" /> Open order
                </Button>
                <Button size="sm" onClick={() => markDelivered(r.id)}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid={`mark-delivered-${r.id}`}>
                  Mark delivered
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
